/**
 * Contact Page Component
 *
 * Contact form for users to get in touch with NCESP
 */

import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import SEOMetaTags from '@/components/SEOMetaTags'
import { getSEOConfig, getCanonicalUrl } from '@/utils/seo'
import { getContactFunctionUrl } from '@/config/api'
import { legalOrganizationInfo } from '@/data/content'

interface ContactFormData {
  name: string
  organization?: string
  email: string
  phone?: string
  message: string
  honeypot?: string
}

/**
 * Contact Page Component
 */
const Contact: React.FC = () => {
  // Get SEO configuration for this page
  const seoConfig = getSEOConfig('/contact')!
  const canonicalUrl = getCanonicalUrl('/contact')

  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      organization: '',
      email: '',
      phone: '',
      message: '',
      honeypot: '',
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Spam prevention: check honeypot field
      // If honeypot is filled, it's likely a bot - silently reject
      if (data.honeypot && data.honeypot.trim() !== '') {
        console.warn('Honeypot field was filled - likely spam')
        // Show success message anyway to not alert spammers
        setSubmitStatus('success')
        reset()
        setTimeout(() => {
          setSubmitStatus('idle')
        }, 5000)
        return
      }

      // Use URLSearchParams to send URL-encoded form data
      // This works with both Netlify dev and production environments
      const params = new URLSearchParams()
      params.append('name', data.name)
      params.append('organization', data.organization || '')
      params.append('email', data.email)
      params.append('phone', data.phone || '')
      params.append('message', data.message)
      params.append('userAgent', navigator.userAgent)
      params.append('timestamp', new Date().toISOString())

      // Submit to Netlify Function (server-side proxy to Google Apps Script)
      // This avoids CORS issues since server-to-server communication is allowed
      const contactUrl = getContactFunctionUrl()
      const response = await fetch(contactUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      if (response.ok) {
        try {
          const result = await response.json()
          console.log('Form response:', result)
        } catch {
          console.log('Response is not JSON, but request succeeded')
        }

        setSubmitStatus('success')
        reset()
        console.log('Contact form submitted successfully:', data)

        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle')
        }, 5000)
      } else {
        throw new Error(`Form submission failed: ${response.status}`)
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
    }
  }

  return (
    <Box component="main" role="main">
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={canonicalUrl}
        ogImage={seoConfig.ogImage}
        keywords={seoConfig.keywords}
        noIndex={seoConfig.noIndex}
      />

      <Container maxWidth="md">
        <Stack spacing={4} sx={{ py: 8 }}>
          {/* Page Header */}
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
            >
              Fill out the form below and we will contact you shortly.
            </Typography>
          </Box>

          {/* Success/Error Messages */}
          {submitStatus === 'success' && (
            <Alert severity="success" onClose={() => setSubmitStatus('idle')}>
              Thank you for your message! We will contact you shortly.
            </Alert>
          )}

          {submitStatus === 'error' && (
            <Alert severity="error" onClose={() => setSubmitStatus('idle')}>
              There was an error submitting your message. Please try again.
            </Alert>
          )}

          {/* Contact Form */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <Stack spacing={3}>
              {/* Honeypot field for spam prevention - hidden from users */}
              <Controller
                name="honeypot"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    aria-hidden="true"
                    tabIndex={-1}
                    sx={{
                      display: 'none',
                      visibility: 'hidden',
                      height: 0,
                      padding: 0,
                      margin: 0,
                      border: 'none',
                    }}
                  />
                )}
              />

              {/* Name Field (Required) */}
              <Controller
                name="name"
                control={control}
                rules={{
                  required: 'Name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    required
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    aria-label="Name (required)"
                  />
                )}
              />

              {/* Organization Field (Optional) */}
              <Controller
                name="organization"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Organization"
                    fullWidth
                    aria-label="Organization (optional)"
                  />
                )}
              />

              {/* Email Field (Required) */}
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    required
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    aria-label="Email (required)"
                  />
                )}
              />

              {/* Phone Field (Optional) */}
              <Controller
                name="phone"
                control={control}
                rules={{
                  pattern: {
                    value: /^[0-9()\-\s+]+$/,
                    message: 'Invalid phone number format',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    type="tel"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    aria-label="Phone (optional)"
                  />
                )}
              />

              {/* Message Field (Required) */}
              <Controller
                name="message"
                control={control}
                rules={{
                  required: 'Message is required',
                  minLength: {
                    value: 10,
                    message: 'Message must be at least 10 characters',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    required
                    fullWidth
                    multiline
                    rows={6}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    aria-label="Message (required)"
                  />
                )}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Send Message
              </Button>
            </Stack>
          </Box>

          <Box
            component="section"
            aria-label="Legal registration address"
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              pt: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              component="h2"
              sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}
            >
              Legal registration address
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {legalOrganizationInfo.legalAddress}.{' '}
              {legalOrganizationInfo.legalAddressNote} Please use the contact
              form above as the primary way to reach NCESP.
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  )
}

export default Contact
